/**
 * Learn's own token store (gairm/lms#391; crmarr/templates auth-self-hosted-sign-in.md, R3), **one per browser**
 * (owner, 2026-10-02; gairm/lms#392): the session lives in localStorage under one key, so a person signs in once and
 * every tab and window of that browser profile carries the same session, renews it together, and is signed out
 * together. It replaced, in this order, oidc-client-ts's per-tab `oidc.user:*` entry and Learn's own per-tab
 * sessionStorage entry (gairm/lms#391); a tab that still holds either is adopted once (`adoptTabEntry`), so nobody is
 * signed out by the deploy.
 *
 * What sharing needs, and where it is done:
 *   - **One renewer at a time** (`withRenewLock`): two tabs must never spend the same refresh token at once. Web Locks
 *     (`navigator.locks`) where the browser has them, a short lease in localStorage where it does not. The tab that waited
 *     reads the renewed tokens from the store rather than renewing again (auth.ts refresh).
 *   - **Every tab hears a change** (`onSessionChange`): the `storage` event fires in every other tab of the origin when the
 *     entry is written or removed — a renewal, a sign-in, a sign-out.
 *   - **A sign-out in progress is everyone's** (`markSigningOut`, `signingOutElsewhere`): while one tab writes the stamp and
 *     revokes, no tab renews or starts a sign-in (the estate sign-out contract, B). The mark is time-limited, so a tab
 *     that dies mid-sign-out cannot hold the others forever.
 *
 * The device slot (R11) is shared with the tokens: one confirmed device per browser, not per tab. The pool tracks
 * devices (lms-dev, 2026-10-01: ChallengeRequiredOnNewDevice and DeviceOnlyRememberedOnUserPrompt both on). The
 * ordering rule every product copies, stated plainly:
 *   1. With device tracking on, Cognito issues a new device key to every API sign-in (InitiateAuth / RespondToAuthChallenge)
 *      that sends none, and that sign-in's refresh token is bound to it.
 *   2. Such a refresh token renews only after ConfirmDevice (with an SRP verifier) and only with that device key on every
 *      refresh: without the key it is refused, and with the key of an unconfirmed device it is refused too (probed on
 *      lms-dev 2026-10-01, GetTokensFromRefreshToken and REFRESH_TOKEN_AUTH alike; /oauth2/token refuses it always).
 *   3. A managed-login sign-in has no device: its refresh token renews with no key.
 *   4. So the store carries the device slot and every refresh path sends the key from it, in the same deploy as (or
 *      before) the first API sign-in whose refresh token the session keeps. Never switch device tracking on, and never
 *      start keeping an API sign-in's refresh token, ahead of that deploy.
 * Since Learn's own sign-in screen (gairm/lms#392) the refresh token kept is the API sign-in's, and the sign-in machine
 * fills the slot: it confirms the device with ConfirmDevice (device-verifier.ts) before handing the tokens over
 * (auth.ts acceptSignIn), and a sign-in whose device could not be confirmed is not kept. A passkey sign-in is issued no
 * device on lms-dev (measured 2026-10-01) and renews with no key; a federated sign-in, through the hosted endpoint, has
 * none either. Step-up's API sign-in keeps the session's own (auth.ts, replaceTokens).
 *
 * **Remembered devices** (gairm/lms#400; R11). When the person ticks "Remember this device", the sign-in machine marks the
 * confirmed device remembered at Cognito (UpdateDeviceStatus) and keeps it in a second entry, `starsach.rememberedDevices`,
 * keyed by the address signed in with: a sign-out ends the session but not the remembering, so the entry outlives the
 * session entry, which every sign-out clears. The next password sign-in for that address sends the key and proves the
 * device's password (device-verifier.ts), and Cognito skips the authenticator code. The entry goes when the device is
 * removed on Account › Security, on Sign out everywhere (which forgets every device at Cognito), and when Cognito no
 * longer knows the device or refuses its proof. The device password is a secret: kept here and nowhere else, never logged
 * or sent (SEC-07); only its SRP proof leaves the browser.
 */
export const SESSION_KEY = "starsach.session";
/** A sign-out in progress in some tab of this browser: the time (epoch ms) the mark lapses. */
export const SIGNING_OUT_KEY = "starsach.signingOut";
/** The renewal lease, where the browser has no Web Locks: `{ owner, until }`. */
export const RENEW_LEASE_KEY = "starsach.renewLease";
/** Long enough for the stamp, the device and the revoke, each bounded at three seconds (auth.ts REVOKE_TIMEOUT_MS). */
export const SIGNING_OUT_MS = 15_000;

/** A device Cognito issued to an API sign-in (`NewDeviceMetadata`), and whether it has been confirmed (ConfirmDevice). */
export interface DeviceSlot {
  key: string;
  groupKey: string;
  /** The device's own password behind its SRP verifier; only a confirmed device has one. */
  password?: string;
  confirmed: boolean;
  /** Remembered at Cognito ("Remember this device", gairm/lms#400): a plain sign-out leaves it, and its entry, in place. */
  remembered?: boolean;
}

/** A remembered device, as `starsach.rememberedDevices` keeps it for one address: what its next sign-in proves itself with. */
export interface RememberedDevice {
  key: string;
  groupKey: string;
  password: string;
}

export const REMEMBERED_DEVICES_KEY = "starsach.rememberedDevices";

function rememberedAll(): Record<string, RememberedDevice> {
  try {
    const all = JSON.parse(window.localStorage.getItem(REMEMBERED_DEVICES_KEY) ?? "{}") as Record<string, Partial<RememberedDevice>>;
    const out: Record<string, RememberedDevice> = {};
    for (const [email, d] of Object.entries(all ?? {})) {
      if (typeof d?.key === "string" && typeof d.groupKey === "string" && typeof d.password === "string") out[email] = { key: d.key, groupKey: d.groupKey, password: d.password };
    }
    return out;
  } catch {
    return {};
  }
}

function saveRemembered(all: Record<string, RememberedDevice>): void {
  try {
    if (Object.keys(all).length === 0) window.localStorage.removeItem(REMEMBERED_DEVICES_KEY);
    else window.localStorage.setItem(REMEMBERED_DEVICES_KEY, JSON.stringify(all));
  } catch {
    // No storage: the device is remembered at Cognito but not here, so the next sign-in asks for the code.
  }
}

/** This browser's remembered device for the address (lower-cased), if it has one. */
export function readRememberedDevice(email: string): RememberedDevice | undefined {
  return rememberedAll()[email.trim().toLowerCase()];
}

/** Keeps the device remembered for the address; one per address, the newest wins. */
export function writeRememberedDevice(email: string, device: RememberedDevice): void {
  saveRemembered({ ...rememberedAll(), [email.trim().toLowerCase()]: { key: device.key, groupKey: device.groupKey, password: device.password } });
}

/**
 * The session's own device is no longer remembered (Account › Security's Remove for this device, gairm/lms#400): the slot
 * keeps the device, which this session still renews with, and drops the mark, so a plain sign-out forgets it as usual.
 */
export function unrememberSessionDevice(key: string): void {
  const t = readTokens();
  if (!t?.device || t.device.key !== key || !t.device.remembered) return;
  const { remembered: _gone, ...device } = t.device;
  writeTokens({ ...t, device });
}

/** Forgets this browser's remembered device: for the address, or wherever it is kept under that device key. */
export function removeRememberedDevice(by: { email?: string; key?: string }): void {
  const all = rememberedAll();
  for (const [email, d] of Object.entries(all)) if ((by.email && email === by.email.trim().toLowerCase()) || (by.key && d.key === by.key)) delete all[email];
  saveRemembered(all);
}

export interface StoredTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** When the ID token runs out (epoch ms): what the API's authorizer checks. */
  expiresAt: number;
  device?: DeviceSlot;
}

function parse(raw: string | null): StoredTokens | null {
  if (!raw) return null;
  try {
    const t = JSON.parse(raw) as Partial<StoredTokens>;
    return typeof t.idToken === "string" && typeof t.accessToken === "string" && Number.isFinite(t.expiresAt) ? (t as StoredTokens) : null;
  } catch {
    return null;
  }
}

export function readTokens(): StoredTokens | null {
  try {
    return parse(window.localStorage.getItem(SESSION_KEY));
  } catch {
    return null;
  }
}

export function writeTokens(t: StoredTokens): void {
  window.localStorage.setItem(SESSION_KEY, JSON.stringify(t));
}

export function clearTokens(): void {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // No storage: nothing was kept.
  }
  try {
    // A tab signed in before the shared store may still hold its own entry, or the library's: they go too.
    window.sessionStorage.removeItem(SESSION_KEY);
    for (const k of Object.keys(window.sessionStorage)) if (k.startsWith("oidc.user:")) window.sessionStorage.removeItem(k);
  } catch {
    // As above.
  }
}

/** When a JWT runs out (epoch ms), from its own `exp`; `fallbackSeconds` from now when it carries none. */
export function expiryOf(jwt: string, fallbackSeconds: number): number {
  try {
    const payload = JSON.parse(atob((jwt.split(".")[1] ?? "").replace(/-/g, "+").replace(/_/g, "/"))) as { exp?: unknown };
    if (typeof payload.exp === "number" && Number.isFinite(payload.exp)) return payload.exp * 1000;
  } catch {
    // Unreadable: the fallback.
  }
  return Date.now() + fallbackSeconds * 1000;
}

/**
 * A tab signed in before the shared store holds a per-tab entry and the browser none: adopt it once, so the owner's
 * open tab carries on rather than meeting the sign-in screen on the first click after the deploy. Learn's own per-tab
 * entry first (gairm/lms#391), then oidc-client-ts's (before it). Whatever this tab held is removed from it either way:
 * when the browser already has a session, that one wins, and the tab joins it.
 */
export function adoptTabEntry(): StoredTokens | null {
  let adopted: StoredTokens | null = null;
  try {
    const own = parse(window.sessionStorage.getItem(SESSION_KEY));
    window.sessionStorage.removeItem(SESSION_KEY);
    if (own) adopted = own;
    for (const k of Object.keys(window.sessionStorage)) {
      if (!k.startsWith("oidc.user:")) continue;
      const u = JSON.parse(window.sessionStorage.getItem(k) ?? "{}") as { id_token?: string; access_token?: string; refresh_token?: string };
      window.sessionStorage.removeItem(k);
      if (!adopted && u.id_token && u.access_token) adopted = { idToken: u.id_token, accessToken: u.access_token, ...(u.refresh_token ? { refreshToken: u.refresh_token } : {}), expiresAt: expiryOf(u.id_token, 0) };
    }
  } catch {
    // Unreadable: no session to adopt.
  }
  if (!adopted) return null;
  if (readTokens()) return readTokens();
  writeTokens(adopted);
  return adopted;
}

/** Marks a sign-out in progress for every tab of this browser, or clears the mark. */
export function markSigningOut(on: boolean, now = Date.now()): void {
  try {
    if (on) window.localStorage.setItem(SIGNING_OUT_KEY, String(now + SIGNING_OUT_MS));
    else window.localStorage.removeItem(SIGNING_OUT_KEY);
  } catch {
    // No storage: the sign-out is this tab's alone, as before.
  }
}

/** Some tab of this browser is signing out right now. */
export function signingOutElsewhere(now = Date.now()): boolean {
  try {
    const until = Number(window.localStorage.getItem(SIGNING_OUT_KEY));
    return Number.isFinite(until) && until > now;
  } catch {
    return false;
  }
}

/**
 * The session as another tab changed it: new tokens (a renewal, a sign-in) or null (a sign-out, a refused renewal). The
 * `storage` event fires only in the tabs that did not make the change, and for `localStorage.clear()` with a null key.
 * Returns the unsubscribe.
 */
export function onSessionChange(listener: (tokens: StoredTokens | null) => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const on = (e: StorageEvent) => {
    if (e.storageArea !== window.localStorage || (e.key !== SESSION_KEY && e.key !== null)) return;
    listener(parse(e.newValue ?? null));
  };
  window.addEventListener("storage", on);
  return () => window.removeEventListener("storage", on);
}

/** How long a tab waits for another tab's renewal before taking the lease itself (the fallback only). */
export const RENEW_LEASE_MS = 12_000;

/**
 * Runs `fn` as this browser's one renewer: no other tab is inside it at the same time. Web Locks where the browser has
 * them (every current browser); otherwise a lease in localStorage, written, read back after a tick to see who won, and
 * waited out while another tab holds it. Either way the caller re-reads the store first thing inside, because the
 * renewal it was about to make may have been made while it waited.
 */
export async function withRenewLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== "undefined" ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
  if (locks?.request) return locks.request("starsach.session.renew", { mode: "exclusive" }, fn) as Promise<T>;
  const me = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const lease = (): { owner: string; until: number } | null => {
    try {
      return JSON.parse(window.localStorage.getItem(RENEW_LEASE_KEY) ?? "null") as { owner: string; until: number } | null;
    } catch {
      return null;
    }
  };
  // Best effort, for a browser with no Web Locks (every current browser has them): free, or lapsed, is taken at once;
  // held by another tab is waited out, at most one lease long, polling.
  const deadline = Date.now() + RENEW_LEASE_MS;
  for (;;) {
    const held = lease();
    if (!held || held.until <= Date.now() || Date.now() >= deadline) {
      try {
        window.localStorage.setItem(RENEW_LEASE_KEY, JSON.stringify({ owner: me, until: Date.now() + RENEW_LEASE_MS }));
      } catch {
        // No storage: no other tab can share a session with this one either.
        return fn();
      }
      if (lease()?.owner === me) break;
    }
    await new Promise((r) => setTimeout(r, 100));
  }
  try {
    return await fn();
  } finally {
    try {
      if (lease()?.owner === me) window.localStorage.removeItem(RENEW_LEASE_KEY);
    } catch {
      // Lapses on its own.
    }
  }
}
